import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { getMentionDirectory } from '@/lib/api';
import {
  activeMentionQuery,
  applyMention,
  mentionProfileLink,
  splitMentions,
} from '@/lib/mentions';

const KIND_LABEL = { user: 'Teammate', student: 'Student', agency: 'Agency' };

const KIND_CHIP = {
  user: 'bg-[#062B1A]/8 text-[#062B1A] border-[#062B1A]/20',
  student: 'bg-[#0B5FBF]/8 text-[#0B5FBF] border-[#0B5FBF]/25',
  agency: 'bg-[#8A6508]/8 text-[#8A6508] border-[#D4AF37]/40',
};

/* Renders stored text with its mention tokens as chips. Student and agency
   chips link to the record; a teammate chip is not a link because there is no
   staff profile page to open. */
export function MentionText({ text, className }) {
  const parts = useMemo(() => splitMentions(text), [text]);
  return (
    <span className={cn('whitespace-pre-wrap break-words', className)}>
      {parts.map((part, index) => {
        if (part.type === 'text') return <span key={index}>{part.value}</span>;
        const chipClass = cn(
          'mx-0.5 inline-flex items-center rounded border px-1 py-px text-[0.92em] font-semibold',
          KIND_CHIP[part.kind] || KIND_CHIP.user,
        );
        const link = mentionProfileLink(part);
        if (!link) {
          return <span key={index} className={chipClass}>@{part.label}</span>;
        }
        return (
          <Link key={index} to={link} className={cn(chipClass, 'hover:underline')}>
            @{part.label}
          </Link>
        );
      })}
    </span>
  );
}

/* A textarea that opens a picker when you type @. */
export default function MentionInput({
  value,
  onChange,
  placeholder,
  disabled,
  rows = 3,
  className,
  onSubmit,
}) {
  const textareaRef = useRef(null);
  const [directory, setDirectory] = useState(null);
  const [range, setRange] = useState(null);
  const [highlight, setHighlight] = useState(0);

  useEffect(() => {
    let cancelled = false;
    getMentionDirectory()
      .then(({ data }) => {
        if (!cancelled) setDirectory(data);
      })
      .catch((error) => {
        console.error(error);
        // Without a directory the field still works as a plain textarea.
        if (!cancelled) setDirectory({ users: [], students: [], agencies: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const options = useMemo(() => {
    if (!range || !directory) return [];
    const query = range.query.trim().toLowerCase();
    const all = [
      ...(directory.users || []),
      ...(directory.students || []),
      ...(directory.agencies || []),
    ];
    const matches = query
      ? all.filter((option) => String(option.label || '').toLowerCase().includes(query))
      : all;
    return matches.slice(0, 8);
  }, [directory, range]);

  const refreshRange = useCallback((text, caret) => {
    const next = activeMentionQuery(text, caret);
    setRange(next);
    setHighlight(0);
  }, []);

  const choose = (option) => {
    if (!range) return;
    const result = applyMention(value, range, option);
    onChange(result.text);
    setRange(null);
    // Put the caret after the inserted token on the next frame, once React has
    // written the new value into the textarea.
    requestAnimationFrame(() => {
      const node = textareaRef.current;
      if (!node) return;
      node.focus();
      node.setSelectionRange(result.caret, result.caret);
    });
  };

  const handleKeyDown = (event) => {
    if (range && options.length) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlight((current) => (current + 1) % options.length);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlight((current) => (current - 1 + options.length) % options.length);
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        choose(options[highlight]);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setRange(null);
        return;
      }
    }
    if (onSubmit && event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      onSubmit();
    }
  };

  return (
    <div className="relative">
      <Textarea
        ref={textareaRef}
        value={value}
        rows={rows}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(event) => {
          onChange(event.target.value);
          refreshRange(event.target.value, event.target.selectionStart);
        }}
        onKeyUp={(event) => refreshRange(event.target.value, event.target.selectionStart)}
        onClick={(event) => refreshRange(event.target.value, event.target.selectionStart)}
        onBlur={() => {
          // Delayed so a click on an option lands before the list unmounts.
          window.setTimeout(() => setRange(null), 150);
        }}
        onKeyDown={handleKeyDown}
        className={className}
      />

      {range && options.length > 0 && (
        <div
          role="listbox"
          aria-label="Mention suggestions"
          className="absolute bottom-full left-0 z-50 mb-1 max-h-64 w-full max-w-sm overflow-y-auto rounded-md border border-[rgba(6,43,26,0.12)] bg-white p-1 shadow-lg"
        >
          {options.map((option, index) => (
            <button
              key={`${option.kind}:${option.id}`}
              type="button"
              role="option"
              aria-selected={index === highlight}
              onMouseDown={(event) => {
                event.preventDefault();
                choose(option);
              }}
              onMouseEnter={() => setHighlight(index)}
              className={cn(
                'flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm',
                index === highlight ? 'bg-[#F1F3F5]' : 'bg-transparent',
              )}
            >
              <span className="min-w-0 flex-1 truncate text-[#062B1A]">{option.label}</span>
              <span className="shrink-0 text-[10px] uppercase tracking-wide text-[#062B1A]/40">
                {option.detail || KIND_LABEL[option.kind]}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
